/**
 * Branding workshop — name search + logo composer.
 *
 * Two tabs inside one page:
 *  01 Naming — bulk-add candidate brand names → check DNS-resolved availability
 *     across .com / .es / .io / etc. + a quick "easy-to-spell" heuristic.
 *  02 Logo composer — pick a Lucide icon + palette + typography, preview the
 *     mark in 4 layouts (badge, horizontal, stacked, monogram). Download SVG.
 *
 * Both are local-state pages; the user can transfer chosen brand name back
 * to the project's concept.company_name with one click.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import {
  ArrowLeft,
  Search,
  Sparkles,
  Plus,
  Trash2,
  Download,
  Wand2,
  Check,
  X,
  Palette,
  Type as TypeIcon,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  PrimaryButton,
  SecondaryButton,
  Overline,
  SubTabs,
  TextInput,
} from "@/components/ui-kit";

// ============================================================================
// Naming sub-page
// ============================================================================

function NamingPanel({ projectId, onPickName, lang }) {
  const [input, setInput] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [tlds, setTlds] = useState(["com", "es", "co", "io"]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  function addFromInput() {
    if (!input.trim()) return;
    const parts = input
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    setCandidates((prev) => [...new Set([...prev, ...parts])]);
    setInput("");
  }

  function removeCandidate(name) {
    setCandidates((prev) => prev.filter((c) => c !== name));
  }

  async function runCheck() {
    if (candidates.length === 0) {
      toast.error(lang === "es" ? "Añade al menos un nombre" : "Add at least one name");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/branding/name-check", { names: candidates, tlds });
      setResults(res.data.results || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function applyNameToProject(name) {
    if (!projectId) {
      toast.error(lang === "es" ? "Sin proyecto asociado" : "No project linked");
      return;
    }
    try {
      // Fetch concept, update company_name, push back
      const project = (await api.get(`/projects/${projectId}`)).data;
      const concept = project.modules?.concept || {};
      const updated = { ...concept, company_name: name };
      await api.put(`/projects/${projectId}/modules/concept`, { data: updated });
      toast.success(
        lang === "es"
          ? `Aplicado "${name}" como nombre del proyecto`
          : `Set "${name}" as project name`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  function toggleTld(tld) {
    setTlds((prev) => (prev.includes(tld) ? prev.filter((t) => t !== tld) : [...prev, tld]));
  }

  return (
    <div className="space-y-6" data-testid="branding-tab-naming">
      <p className="text-sm text-[#5C5B5A] max-w-2xl">
        {lang === "es"
          ? "Brainstorm de nombres → comprobación rápida de disponibilidad de dominio (DNS) + métricas de pronunciación. La señal DNS no sustituye un WHOIS oficial pero es un filtro inicial muy eficaz."
          : "Brainstorm names → quick DNS-based domain availability check + pronounceability metrics. DNS isn't a WHOIS substitute but it's an effective early filter."}
      </p>

      {/* Input area */}
      <div className="border border-[#E8E6E1] bg-white rounded-sm p-5 space-y-4">
        <Overline>{lang === "es" ? "Añade candidatos" : "Add candidates"}</Overline>
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              lang === "es"
                ? "Goldenyears\nMediterana\nBridgemove\n... (uno por línea o separados por coma)"
                : "Goldenyears\nMediterana\nBridgemove\n... (one per line or comma-separated)"
            }
            className="flex-1 border border-[#E8E6E1] rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB]"
            rows={3}
            data-testid="naming-input-textarea"
          />
          <SecondaryButton onClick={addFromInput} testId="naming-add-btn">
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {lang === "es" ? "Añadir" : "Add"}
          </SecondaryButton>
        </div>

        {candidates.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="candidates-chips">
            {candidates.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 bg-[#F5F4F0] border border-[#E8E6E1] rounded-sm px-2.5 py-1 text-sm"
              >
                {c}
                <button onClick={() => removeCandidate(c)} className="text-[#8A8886] hover:text-[#9E2A2B]">
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-end gap-4 pt-2 border-t border-[#E8E6E1]">
          <div>
            <Overline>{lang === "es" ? "Extensiones" : "TLDs"}</Overline>
            <div className="mt-2 flex gap-1">
              {["com", "es", "co", "io", "app", "ai"].map((tld) => {
                const on = tlds.includes(tld);
                return (
                  <button
                    key={tld}
                    type="button"
                    onClick={() => toggleTld(tld)}
                    className={`text-xs font-mono px-2.5 py-1 rounded-sm border transition-colors ${
                      on
                        ? "bg-[#2563EB] text-white border-[#2563EB]"
                        : "bg-white text-[#5C5B5A] border-[#E8E6E1] hover:border-[#1A1A1A]"
                    }`}
                    style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    data-testid={`tld-${tld}-btn`}
                  >
                    .{tld}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="ml-auto">
            <PrimaryButton onClick={runCheck} disabled={loading || candidates.length === 0} testId="naming-check-btn">
              <Search className="h-4 w-4" strokeWidth={1.5} />
              {loading ? (lang === "es" ? "Comprobando…" : "Checking…") : (lang === "es" ? "Comprobar dominios" : "Check domains")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3" data-testid="naming-results">
          <Overline>{lang === "es" ? "Resultados" : "Results"}</Overline>
          {results.map((r) => (
            <div key={r.name} className="border border-[#E8E6E1] rounded-sm bg-white p-5" data-testid={`naming-result-${r.slug}`}>
              <div className="flex items-baseline justify-between gap-4 mb-4">
                <div className="min-w-0">
                  <div className="flex items-baseline gap-3">
                    <h3
                      className="text-2xl text-[#1A1A1A] tracking-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                    >
                      {r.name}
                    </h3>
                    <span
                      className="text-xs text-[#8A8886] font-mono"
                      style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    >
                      /{r.slug}/
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-[#8A8886]">
                    <span>{r.length} {lang === "es" ? "caracteres" : "chars"}</span>
                    <span>~{r.syllables_estimate} {lang === "es" ? "sílabas" : "syllables"}</span>
                    {r.easy_to_spell && (
                      <span className="inline-flex items-center gap-1 text-[#2563EB]">
                        <Check className="h-3 w-3" strokeWidth={2} />
                        {lang === "es" ? "Fácil de deletrear" : "Easy to spell"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div
                      className={`text-3xl font-mono ${r.coverage_score >= 75 ? "text-[#2563EB]" : r.coverage_score >= 50 ? "text-[#B89154]" : "text-[#9E2A2B]"}`}
                      style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    >
                      {r.coverage_score}%
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8886]">
                      {lang === "es" ? "Disponibles" : "Available"}
                    </div>
                  </div>
                  {projectId && (
                    <PrimaryButton onClick={() => applyNameToProject(r.name)} testId={`apply-name-${r.slug}-btn`}>
                      {lang === "es" ? "Aplicar" : "Apply"}
                    </PrimaryButton>
                  )}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {r.domains.map((d) => (
                  <a
                    key={d.host}
                    href={d.available ? `https://www.namecheap.com/domains/registration/results/?domain=${d.host}` : `https://${d.host}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={`group flex items-center justify-between gap-2 px-3 py-2 border rounded-sm text-sm ${
                      d.available
                        ? "border-[#2563EB]/30 bg-[#EFF4FF]/40 hover:border-[#2563EB]"
                        : "border-[#E8E6E1] bg-[#F5F4F0] hover:border-[#5C5B5A]"
                    }`}
                  >
                    <span className="font-mono text-[#1A1A1A]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      {d.host}
                    </span>
                    {d.available ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[#2563EB] uppercase tracking-wider">
                        <Check className="h-3 w-3" strokeWidth={2} />
                        {lang === "es" ? "Libre" : "Free"}
                      </span>
                    ) : (
                      <span className="text-xs text-[#9E2A2B] uppercase tracking-wider">
                        {lang === "es" ? "Tomado" : "Taken"}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Logo composer sub-page
// ============================================================================

const PRESET_NAME_DEFAULT = "Atelier";

function LogoMark({ icon, palette, font, label, size = 220 }) {
  const Icon = LucideIcons[icon] || LucideIcons.Sparkles;
  const iconSize = size * 0.28;
  const fontSize = size * 0.12;
  return (
    <div
      className="rounded-sm relative flex flex-col items-center justify-center gap-3 p-6"
      style={{
        width: size,
        height: size,
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.bg === "#FFFFFF" ? "#E8E6E1" : "transparent"}`,
      }}
      data-testid="logo-preview-mark"
    >
      <div
        className="w-12 h-0.5 mb-1"
        style={{ backgroundColor: palette.accent }}
      />
      <Icon size={iconSize} strokeWidth={1.4} color={palette.accent} />
      <div
        className="leading-none tracking-tight text-center"
        style={{ fontFamily: font.family, fontSize, color: palette.fg, fontWeight: 600 }}
      >
        {label}
      </div>
    </div>
  );
}

function LogoMarkHorizontal({ icon, palette, font, label, width = 360 }) {
  const Icon = LucideIcons[icon] || LucideIcons.Sparkles;
  return (
    <div
      className="rounded-sm flex items-center gap-4 px-5 py-4"
      style={{ width, backgroundColor: palette.bg, color: palette.fg }}
      data-testid="logo-preview-horizontal"
    >
      <Icon size={42} strokeWidth={1.4} color={palette.accent} />
      <div className="flex flex-col gap-0.5">
        <span style={{ fontFamily: font.family, fontSize: 26, fontWeight: 600, lineHeight: 1 }}>
          {label}
        </span>
        <span
          className="text-[10px] uppercase tracking-[0.25em]"
          style={{ color: palette.accent, fontFamily: '"IBM Plex Mono", monospace' }}
        >
          BUSINESS
        </span>
      </div>
    </div>
  );
}

function LogoMonogram({ icon, palette, font, label, size = 120 }) {
  const Icon = LucideIcons[icon] || LucideIcons.Sparkles;
  const initials = (label || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  return (
    <div
      className="rounded-sm flex items-center justify-center relative"
      style={{ width: size, height: size, backgroundColor: palette.bg }}
      data-testid="logo-preview-monogram"
    >
      <span
        style={{ fontFamily: font.family, fontSize: size * 0.42, color: palette.fg, fontWeight: 700 }}
      >
        {initials || "•"}
      </span>
      <Icon size={20} strokeWidth={1.4} color={palette.accent} className="absolute top-2 right-2" />
    </div>
  );
}

function logoToSvg({ icon, palette, font, label }) {
  // Build a simple SVG with the label centred; for the icon we render a "Lucide-ish"
  // representation by adding a circle placeholder + text. (Lucide icons are SVG paths
  // — exporting them faithfully requires fetching the icon source; for v1 we ship a
  // text-based monogram which is the most common brand asset anyway.)
  const safe = (s) => (s || "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]));
  const initials = (label || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "•";
  const fontFamily = font.family.replace(/"/g, "'");
  return (
    `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600">
  <rect width="600" height="600" fill="${palette.bg}"/>
  <rect x="240" y="160" width="120" height="3" fill="${palette.accent}"/>
  <text x="300" y="290" font-family="${fontFamily}" font-size="160" font-weight="700"
        fill="${palette.fg}" text-anchor="middle" dominant-baseline="middle">${safe(initials)}</text>
  <text x="300" y="420" font-family="${fontFamily}" font-size="56" font-weight="600"
        fill="${palette.fg}" text-anchor="middle" dominant-baseline="middle">${safe(label)}</text>
  <text x="300" y="490" font-family="IBM Plex Mono, monospace" font-size="16" fill="${palette.accent}"
        text-anchor="middle" letter-spacing="6">BUSINESS</text>
</svg>`
  );
}

function LogoPanel({ projectId, lang, defaultName, defaultLogo }) {
  const [palettes, setPalettes] = useState([]);
  const [icons, setIcons] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [paletteIdx, setPaletteIdx] = useState(defaultLogo?.palette_idx ?? 0);
  const [iconIdx, setIconIdx] = useState(defaultLogo?.icon_idx ?? 0);
  const [fontIdx, setFontIdx] = useState(defaultLogo?.font_idx ?? 0);
  const [label, setLabel] = useState(defaultLogo?.label || defaultName || PRESET_NAME_DEFAULT);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/branding/config");
        setPalettes(res.data.palettes);
        setIcons(res.data.icons);
        setFonts(res.data.fonts);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoadingCfg(false);
      }
    })();
  }, []);

  const palette = palettes[paletteIdx];
  const icon = icons[iconIdx];
  const font = fonts[fontIdx];

  function downloadSvg() {
    if (!palette || !icon || !font) return;
    const svg = logoToSvg({ icon, palette, font, label });
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(label || "logo").replace(/\s+/g, "_")}_logo.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(lang === "es" ? "Logo SVG descargado" : "SVG logo downloaded");
  }

  async function saveToProject() {
    if (!projectId || !palette || !icon || !font) return;
    setSaving(true);
    try {
      const project = (await api.get(`/projects/${projectId}`)).data;
      const concept = project.modules?.concept || {};
      const updated = {
        ...concept,
        company_name: label || concept.company_name,
        logo: {
          palette_idx: paletteIdx,
          icon_idx: iconIdx,
          font_idx: fontIdx,
          icon_name: icon,
          palette: palette,
          font: font,
          label,
        },
      };
      await api.put(`/projects/${projectId}/modules/concept`, { data: updated });
      toast.success(lang === "es" ? "Logo guardado en el proyecto" : "Logo saved to project");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loadingCfg) {
    return <div className="text-sm text-[#8A8886]">Cargando…</div>;
  }
  if (!palette || !icon || !font) {
    return <div className="text-sm text-[#9E2A2B]">{lang === "es" ? "Sin configuración disponible." : "No config available."}</div>;
  }

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-8" data-testid="branding-tab-logo">
      {/* Previews */}
      <div className="space-y-6">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Overline>{lang === "es" ? "Nombre de marca" : "Brand name"}</Overline>
            <TextInput value={label} onChange={setLabel} testId="logo-label-input" />
          </div>
          <div className="ml-auto flex gap-2">
            <SecondaryButton onClick={downloadSvg} testId="logo-download-svg-btn">
              <Download className="h-4 w-4" strokeWidth={1.5} />
              SVG
            </SecondaryButton>
            {projectId && (
              <PrimaryButton onClick={saveToProject} disabled={saving} testId="logo-save-project-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {saving ? (lang === "es" ? "Guardando…" : "Saving…") : (lang === "es" ? "Guardar en proyecto" : "Save to project")}
              </PrimaryButton>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 items-start">
          <div className="border border-[#E8E6E1] rounded-sm p-6 bg-[#FBFAF7] flex items-center justify-center">
            <LogoMark icon={icon} palette={palette} font={font} label={label} size={240} />
          </div>
          <div className="space-y-5">
            <div className="border border-[#E8E6E1] rounded-sm p-5 bg-[#FBFAF7]">
              <Overline>{lang === "es" ? "Variante horizontal" : "Horizontal lockup"}</Overline>
              <div className="mt-4 flex justify-center">
                <LogoMarkHorizontal icon={icon} palette={palette} font={font} label={label} width={360} />
              </div>
            </div>
            <div className="border border-[#E8E6E1] rounded-sm p-5 bg-[#FBFAF7]">
              <Overline>{lang === "es" ? "Monograma" : "Monogram"}</Overline>
              <div className="mt-4 flex justify-center">
                <LogoMonogram icon={icon} palette={palette} font={font} label={label} size={140} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <aside className="space-y-6">
        <div>
          <Overline><span className="inline-flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" strokeWidth={1.5} />{lang === "es" ? "Paleta" : "Palette"}</span></Overline>
          <div className="mt-3 grid grid-cols-4 gap-2" data-testid="palette-grid">
            {palettes.map((p, i) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPaletteIdx(i)}
                className={`relative aspect-square rounded-sm border-2 transition-all ${
                  i === paletteIdx ? "border-[#2563EB] scale-105" : "border-[#E8E6E1] hover:border-[#8A8886]"
                }`}
                style={{ backgroundColor: p.bg }}
                title={p[`name_${lang}`] || p.name_es}
                data-testid={`palette-${p.key}-btn`}
              >
                <span
                  className="absolute inset-2 rounded-sm"
                  style={{ backgroundColor: p.accent, opacity: 0.5 }}
                />
                <span className="absolute bottom-1 right-1 w-2 h-2 rounded-full" style={{ backgroundColor: p.fg }} />
              </button>
            ))}
          </div>
          <p className="text-xs text-[#8A8886] mt-2">{palette[`name_${lang}`] || palette.name_es}</p>
        </div>

        <div>
          <Overline><span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />{lang === "es" ? "Símbolo" : "Symbol"}</span></Overline>
          <div className="mt-3 grid grid-cols-6 gap-2 max-h-[280px] overflow-y-auto pr-1" data-testid="icon-grid">
            {icons.map((name, i) => {
              const Ic = LucideIcons[name];
              if (!Ic) return null;
              const isActive = i === iconIdx;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setIconIdx(i)}
                  className={`aspect-square rounded-sm border flex items-center justify-center transition-all ${
                    isActive ? "border-[#2563EB] bg-[#EFF4FF]/40" : "border-[#E8E6E1] hover:border-[#5C5B5A]"
                  }`}
                  title={name}
                  data-testid={`icon-${name}-btn`}
                >
                  <Ic size={20} strokeWidth={1.4} color={isActive ? "#2563EB" : "#1A1A1A"} />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Overline><span className="inline-flex items-center gap-1.5"><TypeIcon className="h-3.5 w-3.5" strokeWidth={1.5} />{lang === "es" ? "Tipografía" : "Typography"}</span></Overline>
          <div className="mt-3 space-y-1.5" data-testid="font-list">
            {fonts.map((f, i) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFontIdx(i)}
                className={`w-full text-left px-3 py-2 rounded-sm border transition-all ${
                  i === fontIdx ? "border-[#2563EB] bg-[#EFF4FF]/40" : "border-[#E8E6E1] hover:border-[#5C5B5A]"
                }`}
                style={{ fontFamily: f.family }}
                data-testid={`font-${f.key}-btn`}
              >
                <div className="text-base">Atelier Aa</div>
                <div className="text-[10px] text-[#8A8886] uppercase tracking-[0.2em] mt-0.5">{f.label}</div>
              </button>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================

export default function Branding() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("naming");
  const [defaultName, setDefaultName] = useState("");
  const [defaultLogo, setDefaultLogo] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const project = (await api.get(`/projects/${projectId}`)).data;
        setDefaultName(project.modules?.concept?.company_name || project.name || "");
        setDefaultLogo(project.modules?.concept?.logo || null);
      } catch {
        // ignore
      }
    })();
  }, [projectId]);

  const tabs = [
    { key: "naming", label: lang === "es" ? "Buscador de nombres" : "Name search", icon: Search },
    { key: "logo",   label: lang === "es" ? "Logotipo"            : "Logo composer", icon: Wand2 },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#E8E6E1] bg-white">
        <div className="px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to={projectId ? `/projects/${projectId}` : "/"}
              className="inline-flex items-center gap-1 text-sm text-[#5C5B5A] hover:text-[#1A1A1A]"
              data-testid="back-link"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
              {lang === "es" ? "Volver" : "Back"}
            </Link>
            <div className="h-6 w-px bg-[#E8E6E1]" />
            <div className="min-w-0">
              <Overline>{lang === "es" ? "Branding workshop" : "Branding workshop"}</Overline>
              <h1
                className="text-lg lg:text-xl tracking-tight text-[#1A1A1A] truncate"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                data-testid="branding-title"
              >
                {lang === "es" ? "Nombre + Logotipo" : "Name + Logo"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {projectId && (
              <Link to={`/projects/${projectId}`}>
                <SecondaryButton testId="open-project-btn">
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  {lang === "es" ? "Volver al proyecto" : "Back to project"}
                </SecondaryButton>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="branding-subtab" />
        {tab === "naming" ? (
          <NamingPanel projectId={projectId} lang={lang} />
        ) : (
          <LogoPanel projectId={projectId} lang={lang} defaultName={defaultName} defaultLogo={defaultLogo} />
        )}
      </main>
    </div>
  );
}
